'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { useSupabase } from './use-supabase';

/**
 * @name PRIVATE_PATH_PREFIXES
 * @description A list of private path prefixes
 */
const PRIVATE_PATH_PREFIXES = ['/home', '/admin', '/join', '/update-password'];

/**
 * Paths under private prefixes that are publicly accessible (no auth required).
 * These are community profile pages visible without login.
 */
const PUBLIC_PATH_EXCEPTIONS = [
  /^\/home\/community\/organizations\/[a-z0-9-]+$/,
  /^\/home\/community\/dancers\/[a-zA-Z0-9_-]+$/,
  /^\/home\/events\/[a-f0-9-]+$/,
];

/**
 * @name AUTH_PATHS
 * @description A list of auth paths
 */
const AUTH_PATHS = ['/auth'];

/**
 * @name useAuthChangeListener
 * @param privatePathPrefixes - A list of private path prefixes
 * @param appHomePath - The path to redirect to when the user is signed out
 * @param onEvent - Callback function to be called when an auth event occurs
 */
export function useAuthChangeListener({
  privatePathPrefixes = PRIVATE_PATH_PREFIXES,
  appHomePath,
  onEvent,
}: {
  appHomePath: string;
  privatePathPrefixes?: string[];
  onEvent?: (event: AuthChangeEvent, user: Session | null) => void;
}) {
  const client = useSupabase();
  const pathName = usePathname();
  const hashProcessedRef = useRef(false);

  // Process hash fragment tokens on mount (for magic links from impersonation)
  useEffect(() => {
    const processHashTokens = async () => {
      if (typeof window === 'undefined') return;
      if (hashProcessedRef.current) return;

      const hash = window.location.hash.substring(1);
      if (!hash) return;

      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const type = params.get('type');

      // Only process magic link tokens
      if (accessToken && refreshToken && type === 'magiclink') {
        hashProcessedRef.current = true;

        try {
          const { error } = await client.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('Failed to set session from magic link:', error);
            return;
          }

          // Clean hash from URL to prevent issues on refresh
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          );
        } catch (err) {
          console.error('Error processing magic link tokens:', err);
        }
      }
    };

    processHashTokens();
  }, [client.auth]);

  useEffect(() => {
    // keep this running for the whole session unless the component was unmounted
    const listener = client.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        if (onEvent) {
          onEvent(event, session);
        }

        // log user out if session is falsy
        // and if the current path is a private route
        const shouldRedirectUser =
          !session && isPrivateRoute(pathName, privatePathPrefixes);

        if (shouldRedirectUser) {
          // send user away when signed out
          window.location.assign('/');

          return;
        }

        // revalidate user session when user signs in or out
        if (event === 'SIGNED_OUT') {
          // sometimes Supabase sends SIGNED_OUT event
          // but in the auth path, so we ignore it
          if (AUTH_PATHS.some((path) => pathName.startsWith(path))) {
            return;
          }

          window.location.reload();
        }
      },
    );

    // destroy listener on un-mounts
    return () => listener.data.subscription.unsubscribe();
  }, [client.auth, pathName, appHomePath, privatePathPrefixes, onEvent]);
}

/**
 * Determines if a given path is a private route.
 */
function isPrivateRoute(path: string, privatePathPrefixes: string[]) {
  // Check if this is a public exception first
  if (PUBLIC_PATH_EXCEPTIONS.some((pattern) => pattern.test(path))) {
    return false;
  }

  return privatePathPrefixes.some((prefix) => path.startsWith(prefix));
}
